import { Box } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1LoadingButton } from 'components/core/button/index'
import { C1Icon } from 'components/core/icons/C1Icon'
import { RecommendationReviewTooltip } from 'components/core/insights'
import { TaskPrimaryActionsV2Variant } from 'components/core/task-list-v2/task-list-v2-components/TaskPrimaryActionsV2'
import { ApproveIcon } from 'components/pages/task/action/button'
import { ApproveText, getApproveText } from 'components/pages/task/action/messages'
import { ApproveWithCommentTaskDialog } from 'components/pages/task/details/task-content-v2/common/ApproveWithCommentTaskDialog'
import { canActOn } from 'components/pages/task/state'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import { useSimpleModal } from 'lib/modals'
import { Check } from 'lucide-react'
import { TaskActionType } from 'pbts/c1/api/task/v1/task'
import { FC, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useTicketActionTracker } from 'redux_toolkit/lib/TicketActionTracker'

export const ApprovePrimaryAction: FC<{ task: TaskValue, variant?: TaskPrimaryActionsV2Variant }> = ({ task, variant = 'default' }) => {
    const intl = useIntl()
    const { open } = useSimpleModal()
    const toast = useAlert()
    const ticketActionTracker = useTicketActionTracker()

    const handleClick = useCallback(async (): Promise<unknown> => {
        const instance = task.policy?.current?.instance
        const requiresComment =
            instance && instance.oneofKind === 'approval' && instance.approval.approval?.requireApprovalReason
        if (requiresComment) {
            return open(ApproveWithCommentTaskDialog, { task })()
        }
        try {
            await ticketActionTracker?.approve(task)
        } catch (err) {
            toast(
                intl.formatMessage(
                    {
                        id: 'task_approve_error',
                        defaultMessage: 'Error approving task. {code}: {msg}',
                    },
                    {
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    },
                ),
                'error',
            )
        }
        return Promise.resolve()
    }, [intl, open, task, ticketActionTracker, toast])

    if (!canActOn(task, TaskActionType.APPROVE)) {
        return null
    }

    return (
        <RecommendationReviewTooltip task={task}>
            <Box>
                <C1LoadingButton
                    startIcon={<ApproveIcon task={task} />}
                    variant={variant === 'contained' ? 'contained' : 'outlined'}
                    key={`components_task_list_approve_option_${task.id}`}
                    onClick={handleClick}
                    loading={!ticketActionTracker}
                    color={variant === 'default' ? 'primary' : 'success'}
                    sx={variant === 'icon-button' ? { minWidth: 0 } : {}}
                    aria-label={variant === 'icon-button' ? getApproveText(intl, task) : undefined}
                >
                    {variant === 'icon-button' ? <C1Icon icon={Check} /> : <ApproveText task={task} />}
                </C1LoadingButton>
            </Box>
        </RecommendationReviewTooltip>
    )
}
